import { normalizeVenueId, resolveVenueIdFromLocation } from './venueParam';

let currentBlobUrl;
let currentManifestJson;

// The text under the Home Screen icon. Phones show about a dozen characters and shorten the rest themselves, so:
//   1. the venue's alias, when it has one that is not just its full name again;
//   2. otherwise its name without the words at the end that only say what kind of place it is
//      ("Moonlight Bay Suites" -> "Moonlight Bay"); a name is never cut short in the middle;
//   3. otherwise its name as it is. The platform name is only used until the venue's name is known.
const FALLBACK_LABEL = 'P360-DG';

// Zero-width characters would leave text that looks empty but is not, so they go too.
const tidy = (text) => String(text || '').replace(/[\u200B-\u200D\u2060\uFEFF]/g, '').replace(/\s+/g, ' ').trim();

const KIND_WORDS = new Set(['hotel', 'hotels', 'resort', 'resorts', 'motel', 'motels', 'apartment', 'apartments', 'suite', 'suites', 'lodge', 'villa', 'villas']);
const JOINERS = new Set(['and', '&']);

const dropKindWords = (name) => {
  const words = name.split(' ');
  while (words.length > 1) {
    const last = words[words.length - 1].toLowerCase();
    const before = (words[words.length - 2] || '').toLowerCase();
    if (KIND_WORDS.has(last) || JOINERS.has(last)) words.pop();
    else if (last === 'inn' && before === 'motor' && words.length > 2) words.splice(-2, 2); // "Motor Inn", but not the "Inn" of a brand
    else break;
  }
  const shortened = words.join(' ');
  return /^(the|a|an)$/i.test(shortened) ? name : shortened; // "The Lodge" must not become "The"
};

export const homeScreenLabel = (venueName, alias) => {
  const name = tidy(venueName);
  const own = tidy(alias);
  if (own && own.toLowerCase() !== name.toLowerCase()) return own;
  return name ? dropKindWords(name) : FALLBACK_LABEL;
};

// The venue gate asks for the manifest before the venue has loaded, and again when the venue changes, without a name.
// Such a call keeps the name and alias already known for that venue instead of falling back to the platform name.
const knownNames = new Map();

// iPhone and iPad take the Home Screen name from this tag, not from the manifest.
const IOS_TITLE_META = 'meta[name="apple-mobile-web-app-title"]';
const setIosTitle = (label) => {
  let meta = document.querySelector(IOS_TITLE_META);
  if (!label) {
    if (meta) meta.remove();
    return;
  }
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute('name', 'apple-mobile-web-app-title');
    document.head.appendChild(meta);
  }
  meta.setAttribute('content', label);
};

export const createVenueManifest = (venueId, venueName, origin = window.location.origin, alias) => {
  const id = normalizeVenueId(venueId);
  if (!id) return null;
  const home = new URL('/', origin);
  home.searchParams.set('venue_id', id);
  home.searchParams.set('source', 'pwa');
  const appId = home.href;
  return {
    id: appId,
    short_name: homeScreenLabel(venueName, alias),
    name: (venueName || id.replace(/_/g, ' ')) + ' - Digital Compendium',
    icons: [
      { src: new URL('/favicon.ico', origin).href, sizes: '64x64 32x32 24x24 16x16', type: 'image/x-icon' },
      { src: new URL('/logo192.png', origin).href, sizes: '192x192', type: 'image/png', purpose: 'any maskable' },
      { src: new URL('/logo512.png', origin).href, sizes: '512x512', type: 'image/png', purpose: 'any maskable' },
    ],
    start_url: home.href,
    scope: new URL('/', origin).href,
    display: 'standalone',
    theme_color: '#000000',
    background_color: '#ffffff',
  };
};

export const updateVenueManifest = (venueId = resolveVenueIdFromLocation(), venueName, alias) => {
  const id = normalizeVenueId(venueId);
  if (id && tidy(venueName)) knownNames.set(id, { name: venueName, alias });
  const known = tidy(venueName) ? { name: venueName, alias } : (id && knownNames.get(id)) || {};
  const manifest = createVenueManifest(venueId, known.name, undefined, known.alias);
  let link = document.querySelector('link[rel="manifest"]');
  if (!manifest) {
    if (link) link.remove();
    if (currentBlobUrl) URL.revokeObjectURL(currentBlobUrl);
    currentBlobUrl = undefined;
    currentManifestJson = undefined;
    knownNames.clear();
    setIosTitle(null);
    document.title = 'Platypus360 - DG';
    return;
  }
  const json = JSON.stringify(manifest);
  if (link?.getAttribute('href') !== currentBlobUrl || json !== currentManifestJson) {
    const blobUrl = URL.createObjectURL(new Blob([json], { type: 'application/manifest+json' }));
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'manifest');
      document.head.appendChild(link);
    }
    link.setAttribute('href', blobUrl);
    if (currentBlobUrl) URL.revokeObjectURL(currentBlobUrl);
    currentBlobUrl = blobUrl;
    currentManifestJson = json;
  }
  setIosTitle(manifest.short_name === FALLBACK_LABEL ? null : manifest.short_name);
  document.title = manifest.name;
  return manifest;
};
